import React, { useEffect, useState } from "react";
import Image from "components/image";
import { useMediaQuery } from "react-responsive";
import MediaQuery from "components/media-query";
import { SCREEN_SIZES } from "utils/constants";
import { useSettings } from "hooks/useStore";
import { ReactComponent as PlusIcon } from "svgs/plus.svg";
import { ReactComponent as MinusIcon } from "svgs/minus.svg";
import { sortTabs } from "helpers/tabs";
import clsx from "clsx/lite";
import ExpandedMedia from "./expanded-media";
import DefaultTabContent from "./default-content";

const TabTitle = ({ tab, isActive, onClick, isMobile, index, sortedTabs }) => {
	const handleClick = (e) => {
		onClick(index);

		if (isMobile) {
			const element = e.target.parentElement;
			if (element) {
				window.scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - 200, behavior: "smooth" });
			}
		}
	};

	return (
		<button
			className={clsx("tab-title", isActive ? "active-tab-title" : "text-secondary rounded")}
			onClick={handleClick}
		>
			<div className="flex gap-2 whitespace-nowrap items-center">
				<Image image={tab.icon} className="w-5" />
				<h5 className={clsx(sortedTabs.length === 4 ? "text-xl" : "text-base", " font-base")}>
					{tab.title}
				</h5>
			</div>
			<div className="md:hidden">
				{isActive ? <MinusIcon className="fill-black" /> : <PlusIcon />}
			</div>
		</button>
	);
};

const TabContent = ({ activeTabData, crawlText, logistics }) => {

	if (activeTabData?.layout === "expandedMedia") {
		return <ExpandedMedia data={activeTabData} />;
	}

	return <DefaultTabContent activeTabData={activeTabData} crawlText={crawlText} logistics={logistics} />;
};

const Tabs = ({ tabs, productId, crawlText, logistics }) => {
	const isMobile = useMediaQuery({ maxWidth: SCREEN_SIZES.md });
	const { updateActiveTab, productSettings } = useSettings(["updateActiveTab", "productSettings"]);
	const savedActiveTab = productSettings?.[productId]?.activeTab ?? null;
	const [activeTabKey, setActiveTabKey] = useState(null);

	const sortedTabs = sortTabs(tabs);

	useEffect(() => {
		const defaultTab = isMobile ? null : 0;
		setActiveTabKey(savedActiveTab ?? defaultTab);
	}, [isMobile, savedActiveTab]);

	const handleTabChange = (key) => {
		const newKey = activeTabKey !== key ? key : isMobile ? null : activeTabKey;
		setActiveTabKey(newKey);
		updateActiveTab(productId, newKey);
	};

	const activeTabData = sortedTabs?.[activeTabKey];
	return (
		<div className="w-full flex flex-col gap-6 overflow-hidden">
			<div className="flex justify-start flex-col gap-4 md:gap-0 md:flex-row overflow-auto px-4 lg:px-0">
				{sortedTabs.map((tab, index) => (
					<div key={index} className="flex flex-col gap-4 w-full">
						<TabTitle
							tab={tab}
							isActive={index === activeTabKey}
							onClick={handleTabChange}
							isMobile={isMobile}
							index={index}
							sortedTabs={sortedTabs}
						/>
						{activeTabData?._key === tab._key &&
							<MediaQuery maxWidth={SCREEN_SIZES.md}>
								<div className={clsx("w-full px-2 md:hidden", activeTabKey !== index && "hidden")}>
									<TabContent activeTabData={activeTabData} crawlText={crawlText} logistics={logistics} />
								</div>
							</MediaQuery>}
					</div>
				))}
			</div>
			<MediaQuery minWidth={SCREEN_SIZES.md}>
				<div className="w-full hidden md:flex flex-col">
					<TabContent activeTabData={activeTabData} crawlText={crawlText} logistics={logistics} />
				</div>
			</MediaQuery>
		</div>
	);
};

export default Tabs;